import React, { useEffect, useRef, useState } from 'react';
import { getAllCompanyStaffById, getAllNonMotorPolicies } from '../../../../api/requests';
import GTable from '../../../../components/elements/GTable';
import Page from '../../../../components/PageWrapper';
import { Policy } from '../../../../models/policy/Policy';
import { capitalizeSentence, getFormattedAccessRight, getOrUpdateQueriesFromUrl, getPhoneNumberFromShortCodeFormat, getPolicyReviewStatus, numberWithCommas, onRemoveFilter, showToast, updateQueriesList } from '../../../../utils/miscFunctions';
import moment from 'moment';
import clsx from 'clsx';
import { COMPANY_TYPES, POLICY_REVIEW_STATES, POLICY_STATUS_OPTIONS } from '../../../../resources/enums';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GTableActionColumn, ITablePaginationData, ITableRowAction } from '../../../../components/elements/GTable/GTable';
import useUserAccount from '../../../../hooks/userAccountHook';
import useRoute from '../../../../hooks/routesHook';
import usePolicy from '../../../../hooks/policyHook';
import useModal from '../../../../hooks/modalHook';
import { ITableFilterItem } from '../../../../resources/interfaces';
import PolicyStatus from '../../../../components/elements/PolicyStatus';
import TableFilter from '../../../../components/elements/GTable/InGTableComponents/TableFilter/index';
import TableFilterForm from '../../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import useDownload from '../../../../hooks/downloadHook';
import { useQuery } from '@tanstack/react-query';
import useLayoutHook from '../../../../hooks/layoutHook';
import { getEnumsFromState, getSelectedCountry } from '../../../../states/slices/resourceSlice';
import { useSelector } from 'react-redux/es/hooks/useSelector';
import Button from '../../../../components/form-control/Button';
import { Clipboard, Edit, Eye, Plus, Trash } from 'lucide-react';
import usePartner from '../../../../hooks/partnerHook';
import NonAutoPolicySummary from '../../../../models/nonAutoPolicy/NonAutoPolicySummary';
import IconAction from '../../../../components/elements/GTable/InGTableComponents/IconAction';
import { TbClipboardCopy } from 'react-icons/tb';
import { FaLink } from 'react-icons/fa6';
import { LuExternalLink } from 'react-icons/lu';
import { getAllInsurers } from '../../../../services/insurer';
import { useClaim } from '../../../../hooks/claimHook';
import Tooltip from '../../../../components/ToolTip';
import { CUSTOMER_TYPE } from '../../../../resources/enums';
import RouteLink from '../../../../components/elements/GTable/InGTableComponents/RouteLink';
import { useTableFilters } from '../../../../hooks/useTableFilters';
import Banner from '../../../../components/ui/Banner';

function Policies(): JSX.Element {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useUserAccount();
    const companyId = user?.company?.id;
    const enums = useSelector(getEnumsFromState);
    const { POLICY_FILTERS, PolicyModalComponent, PolicyTypesBadge, handleDeletePolicyByNumber, policiesCount } = usePolicy();
    const { fetchAllCompaniesByType } = usePartner();
    const { getDashboardBase } = useRoute();
    const navigate = useNavigate();
    const { ModalComponent } = useModal();
    const { screenWidth } = useLayoutHook();
    const [searchString, setSearchString] = useState<string>();
    const [showPerPage, setShowPerPage] = useState<number>(25);
    const [networkError, setNetworkError] = useState<boolean>(false)
    const [filter, setFilter] = useState<POLICY_REVIEW_STATES | 'All' | undefined>(getInitFilters());
    // const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
    const currentPage = Number(searchParams.get("page")) || 0;
    const [insurers, setInsurers] = useState<any>([]);
    const [brokers, setBrokers] = useState<any>([]);
    const [admins, setAdmins] = useState<any[]>([]);
    const pageRef = useRef<HTMLDivElement>(null)
    //const [tableFilters, setTableFilters] = useState<ITableFilterItem[]>(getDefaultFilters());
    const { createClaim, ClaimModal } = useClaim();

    const [openFilter, setOpenFilter] = useState<boolean>(false);
    const { DownloadHookModal } = useDownload();
    const baseUrl = window.location.origin;
    const selectedCountry = useSelector(getSelectedCountry);
    const countryId = selectedCountry?.id ?? 1;
    const isBusinessCustomer = CUSTOMER_TYPE.BUSINESS
    const isIndividualCustomer = CUSTOMER_TYPE.INDIVIDUAL
    const queriesList: Record<string, string> = {
        type: 'non-motor',
        page: String(currentPage)
    }

    const {
        filters: tableFilters,
        setFilters,
        getFilterValue,
    } = useTableFilters({
        initialFilters: [],
        syncWithUrl: true,
    });

    const queriesName = [
        'active',
        'broker',
        'insurer',
        'policy_type',
        'review_stage'
    ];

    useEffect(() => {
        setFilters(getDefaultFilters());
    }, [insurers, brokers]);


    function getDefaultFilters() {
        const filters: ITableFilterItem[] = [
            {
                name: 'active',
                value: searchParams.get('active') ? String(searchParams.get('active')) : '',
                type: 'select',
                list: [{ name: 'Active', value: 'active' }, { name: 'Inactive', value: 'inactive' }],
                placeholder: 'Select active',
                label: 'Status'
            },
            {
                name: 'review_stage',
                value: searchParams.get('review_stage') ? String(searchParams.get('review_stage')) : '',
                type: 'select',
                list: POLICY_FILTERS,
                placeholder: 'Select status',
                label: 'Status',
                showSearch: true
            },
            {
                name: 'policy_type',
                value: searchParams.get('policy_type') ? String(searchParams.get('policy_type')) : '',
                type: 'select',
                list: Object.values(enums?.policyTypes)?.filter((type: any) => type.key !== 'VEHICLE')?.map((type: any) => ({ name: type.title, value: type.key })),
                placeholder: 'Select policy type',
                label: 'Policy type',
                showSearch: true
            },
            {
                name: 'expiry_date',
                value: '',
                type: 'dateRange',
                placeholder: 'Select expiry date',
                label: 'Expiry date'
            },
            {
                name: 'inception_date',
                value: '',
                type: 'dateRange',
                placeholder: 'Select inception date',
                label: 'Inception date'
            }
        ];

        if (user?.isInternalAdmin?.() || user?.isInsurer?.()) {
            filters.unshift({
                name: 'broker',
                value: searchParams.get('broker') ? String(searchParams.get('broker')) : '',
                value2: searchParams.get('broker') ? String(searchParams.get('broker')) : '',
                type: 'select',
                list: brokers,
                placeholder: 'Select broker',
                label: 'Broker',
                showSearch: true
            })
        }

        if (!(user.isInsurer() || user.isAdministrator() || user.isAgent())) {
            filters.unshift(
                {
                    name: 'manager',
                    value: searchParams.get('manager') ? String(searchParams.get('manager')) : '',
                    value2: searchParams.get('manager') ? String(searchParams.get('manager')) : '',
                    type: 'select',
                    list: admins,
                    placeholder: `Select policy manager`,
                    label: `Policy manager`
                },
            );
        }

        if (user?.isInternalAdmin?.() || user?.isBroker?.()) {
            filters.unshift({
                name: 'insurer',
                value: searchParams.get('insurer') ? String(searchParams.get('insurer')) : '',
                value2: searchParams.get('insurer') ? String(searchParams.get('insurer')) : '',
                type: 'select',
                list: insurers,
                placeholder: 'Select insurer',
                label: 'Insurer',
                showSearch: true
            })
        }

        return filters;
    }

    const getAllBrollyInsurers = () => {
        getAllInsurers(countryId)
            .then((response: any) => {
                const allInsurers = response?.data?.map((insurer: any) => ({
                    name: `${insurer.name}`,
                    value: Number(insurer.id)
                }));
                setInsurers(allInsurers);
            })
            .catch(() => { })
    };

    const getAllBrollyBrokers = async () => {
        try {
            const response = await fetchAllCompaniesByType(COMPANY_TYPES.BROKER);
            const allBrokers = response?.map((broker: any) => ({
                name: `${broker.name}`,
                value: Number(broker.id)
            }));
            setBrokers(allBrokers);
        } catch (error) {

        }
    };

    const getAllAdmins = () => {
        companyId && getAllCompanyStaffById(Number(companyId))
            .then((response: any) => {
                //const managersList = user.isBroker() ? response?.filter((admin: any) => admin?.company?.type === COMPANY_TYPES.BROKER && admin?.company?.name === user.company?.name) : response;
                const _admins = response.filter((admin: any) => admin.activated === true)?.map((admin: any) => ({
                    name: `${admin.name}`,
                    value: admin.id,
                }));
                setAdmins(_admins);
            })
            .catch(() => { });
    };

    useQuery({ queryKey: ['getAllAdmins'], queryFn: getAllAdmins });

    function getInitFilters(): POLICY_REVIEW_STATES | 'All' {
        switch (searchParams.get('filter')?.toLowerCase()) {
            case 'in_approval':
                return POLICY_REVIEW_STATES.IN_APPROVAL;

            case 'approved':
                return POLICY_REVIEW_STATES.APPROVED;

            case 'in_review':
                return POLICY_REVIEW_STATES.IN_REVIEW;

            case 'new':
                return POLICY_REVIEW_STATES.NEW;

            case 'declined':
                return POLICY_REVIEW_STATES.DECLINED;

            case 'cancelled':
                return POLICY_REVIEW_STATES.CANCELLED;

            case 'expired':
                return POLICY_REVIEW_STATES.EXPIRED;

            case 'archived':
                return POLICY_REVIEW_STATES.ARCHIVED;

            case 'due_later':
                return POLICY_REVIEW_STATES.DUE_LATER;

            default:
                return 'All';
        }
    }

    const searchParam = searchString;
    const inceptionDateFilter = getFilterValue('inception_date') || {};
    const endsOn = inceptionDateFilter?.to;
    const startsOn = inceptionDateFilter?.from;
    const expiryDateFilter = getFilterValue('expiry_date') || {};
    const expiredTo = searchParams.get('expiry_date_to') ? searchParams.get('expiry_date_to') : expiryDateFilter?.to;
    const expiredFrom = searchParams.get('expiry_date_from') ? searchParams.get('expiry_date_from') : expiryDateFilter?.from;
    const active = searchParams.get('active') ?? tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'active')?.value ?? '';
    const policyType = searchParams.get('policy_type') ? searchParams.get('policy_type') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'policy_type')?.value;
    const reviewStage = searchParams.get('review_stage') ? searchParams.get('review_stage') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'review_stage')?.value;
    const insurerId = searchParams.get('insurer') ? searchParams.get('insurer') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'insurer')?.value
    const brokerId = searchParams.get('broker') ? searchParams.get('broker') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'broker')?.value
    const managerId = searchParams.get('manager') ? searchParams.get('manager') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'manager')?.value

    const getNonAutoPolicies = async () => {
        try {
            const activeParam = active === 'active' ? 'true' : active === 'inactive' ? 'false' : undefined;
            const response: any = await getAllNonMotorPolicies(currentPage, showPerPage, activeParam, brokerId, searchParam, insurerId, policyType, reviewStage, managerId, expiredFrom, expiredTo, startsOn, endsOn)

            return response;

        } catch (error: any) {
            showToast.error(error.name, error.description);
            setNetworkError(true)
        } finally {

        }
    };

    const scrollToTop = () => {
        if (pageRef.current) {
            pageRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        scrollToTop()
    }, [currentPage])


    const getTableRowActions = (data: NonAutoPolicySummary): ITableRowAction[] => {
        const actions = [
            {
                label: 'View',
                icon: <Eye className='h-4 w-4' />,
                callback: (policy: NonAutoPolicySummary) => {
                    navigate(`${getDashboardBase()}/policies/${policy?.policyNumber}?type=non-motor`, { replace: true });
                },
                data: data
            },
            {
                label: 'Delete',
                icon: <Trash className='h-4 w-4' />,
                callback: () => {
                    handleDeletePolicyByNumber(data?.policyNumber);
                },
                data: data
            }
        ];

        if ((user?.isOwner() || user?.isSupervisor())) {
            actions.push({
                label: 'Edit',
                icon: <Edit className="h-4 w-4" />,
                callback: (policy: NonAutoPolicySummary) => {
                    navigate(`${getDashboardBase()}/policies/${policy?.policyNumber}/edit?type=non-motor`, { replace: true });
                },
                data: data
            });
        }

        if (data?.active) {
            actions.push({
                label: 'Make Claim',
                icon: <Clipboard className="h-4 w-4" />,
                callback: (policy: NonAutoPolicySummary) => createClaim(policy?.policyNumber, false),
                data: data
            })
        }



        return actions;
    };

    const columns = React.useMemo(
        () => [
            {
                id: 'selection',
                meta: {
                    sticky: 'left',
                    offset: -1
                },
                Header: ({ getToggleAllRowsSelectedProps }: any) => (
                    <div className="">
                        <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
                    </div>
                ),
                Cell: ({ row }: any) => (
                    <div className="">
                        <input type="checkbox" {...row.getToggleRowSelectedProps()} />
                    </div>
                )
            },
            {
                Header: 'M',
                columns: [
                    {
                        Header: 'Insurer Policy #',
                        accessor: 'insurerPolicyNumber',
                        meta: {
                            sticky: 'left',
                            offset: 40
                        },
                        Cell: (row: any) => {
                            return (
                                <RouteLink to={`${getDashboardBase()}/policies/${row?.row?.original.policyNumber}?type=non-motor`}>
                                    {row.cell.value ?? 'Awaiting Policy Number'}
                                </RouteLink>
                            );
                        }
                    },
                    // {
                    //     Header: ' ',
                    //     accessor: ' ',
                    //     meta: {
                    //         sticky: 'left',
                    //         offset: 210
                    //     },
                    //     Cell: (row: any) => {
                    //         return (
                    //             <>
                    //                 <IconAction
                    //                     messageIconOne={`Copy policy ID`}
                    //                     IconOne={TbClipboardCopy}
                    //                     onclickIconOne={async () => {
                    //                         await navigator.clipboard.writeText(`${row?.row?.original.policyNumber}`);
                    //                     }}

                    //                     messageIconTwo={`Copy policy link`}
                    //                     IconTwo={FaLink}
                    //                     onclickIconTwo={async () => {
                    //                         await navigator.clipboard.writeText(
                    //                             baseUrl +
                    //                             '/admin/' +
                    //                             `policies/${row?.row?.original.policyNumber}`
                    //                         );
                    //                     }}

                    //                     messageIconThree={`Open in a new tab`}
                    //                     IconThree={LuExternalLink}
                    //                     linkTo={`${getDashboardBase()}/policies/${row?.row?.original.policyNumber}?type=non-motor`}
                    //                 />

                    //             </>
                    //         );
                    //     }
                    // },
                    {
                        Header: 'Customer',
                        accessor: 'customerFullName',
                        meta: {
                            sticky: 'left',
                            offset: 190
                        },
                        Cell: (row: any) => {
                            const isCustomerTypeIndividual = row?.row?.original?.customerType === isIndividualCustomer;
                            const isCustomerTypeBusiness = row?.row?.original?.customerType === isBusinessCustomer;
                            const fullName = row?.row?.original?.customerFirstName + " " + row?.row?.original?.customerLastName


                            return (
                                <RouteLink to={`${getDashboardBase()}/customers/${row.row.original?.customerId}`} showTooltip toolTipMessage={fullName} tooltipDisabled={fullName.length < 25}>
                                    {fullName && capitalizeSentence(fullName)} {isCustomerTypeBusiness && row?.row?.original?.businessName && ` - ${capitalizeSentence(row?.row?.original?.businessName)}`}
                                </RouteLink>

                                // <div className="flex items-center gap-1">

                                //     <RouteLink to={`${getDashboardBase()}/customers/${row.row.original?.customerId}`} showTooltip toolTipMessage={fullName} tooltipDisabled={fullName.length < 25}>
                                //         {fullName && capitalizeSentence(fullName)} {isCustomerTypeBusiness && row?.row?.original?.businessName && ` - ${capitalizeSentence(row?.row?.original?.businessName)}`}
                                //     </RouteLink>

                                //     {isCustomerTypeIndividual ?
                                //         <Tooltip message="Individual Customer" placement='bottom'>
                                //             <span className="inline-flex items-center justify-center w-4 h-4 ml-1 text-xs font-bold text-blue-600  rounded-sm border border-blue-600 dark:text-yellow-600 dark:border-yellow-600">
                                //                 i
                                //             </span>
                                //         </Tooltip>
                                //         : isCustomerTypeBusiness ?
                                //             <Tooltip message="Business Customer" placement='bottom'>
                                //                 <span className="inline-flex items-center justify-center w-4 h-4 ml-1 text-xs font-bold text-blue-600  rounded-sm border border-blue-600 dark:text-yellow-600 dark:border-yellow-600">
                                //                     b
                                //                 </span>
                                //             </Tooltip>
                                //             : <></>
                                //     }
                                // </div>
                            );
                        }
                    },
                    ...(user?.isInternalAdmin() ? [{
                        Header: 'Brolly Policy ID',
                        accessor: 'policyNumber',
                        Cell: (row: any) => {
                            const isRenewed = row?.row?.original?.renewed;

                            return (
                                <Link
                                    to={`${getDashboardBase()}/policies/${row?.cell?.value}?type=non-motor`}>
                                    <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                                </Link>
                                // <div className="flex items-center gap-1">
                                //     <Link
                                //         to={`${getDashboardBase()}/policies/${row?.cell?.value}?type=non-motor`}>
                                //         <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                                //     </Link>

                                //     {isRenewed && (
                                //         <Tooltip message="Renewed policy" placement='bottom'>
                                //             <span className="inline-flex items-center justify-center w-4 h-4 ml-1 text-xs font-bold text-green-600 bg-green-50 rounded-sm border border-green-600 dark:text-green-400 dark:bg-green-900/20 dark:border-green-400 transition-colors duration-200">
                                //                 r
                                //             </span>
                                //         </Tooltip>
                                //     )}
                                // </div>
                            );
                        }
                    }] : []),
                    {
                        Header: 'Phone #',
                        accessor: 'customerPhone',
                        Cell: (row: any) => {
                            return <span>{getPhoneNumberFromShortCodeFormat(row.cell.value) ?? '-'}</span>;
                        }
                    },
                    {
                        Header: 'Policy Type',
                        accessor: 'policyType',
                        Cell: (row: any) => {
                            return (
                                <PolicyTypesBadge policyType={row.cell.value} />
                            );
                        }
                    },
                    {
                        Header: 'Status',
                        accessor: 'reviewStage',
                        Cell: (row: any) => {
                            return (
                                <PolicyStatus
                                    status={getPolicyReviewStatus(row.cell.value)}
                                    classes="px-2 w-full"
                                />
                            );
                        }
                    },
                    // {
                    //     Header: 'Paid to insurer',
                    //     accessor: 'premiumPaidToInsurer',
                    //     Cell: (row: any) => {
                    //         return (
                    //             row.cell.value ?
                    //                 <div className='px-2 border  border-[#0F7125] text-xs flex  h-[24px] w-fit items-center justify-center text-[#0F7125] rounded-full'>
                    //                     Paid
                    //                 </div> :
                    //                 <div className='px-2 border border-red-800 text-xs flex  h-[24px] w-fit items-center justify-center text-red-800 rounded-full'>
                    //                     Unpaid
                    //                 </div>
                    //         );
                    //     }
                    // },
                    {
                        Header: 'Currency',
                        accessor: 'currency',
                        Cell: (row: any) => {
                            return (
                                <div className="min-w-[100px]">
                                    <span>{row.cell.value}</span>
                                </div>
                            );
                        }
                    },
                    {
                        Header: 'Premium',
                        accessor: 'grossPremium',
                        Cell: (row: any) => {
                            return (
                                <div className="min-w-[100px]">
                                    <span>{numberWithCommas(row.cell.value)}</span>
                                </div>
                            );
                        }
                    },
                    {
                        Header: 'Inception Date',
                        accessor: 'inceptionDate',
                        Cell: (row: any) => {
                            return (
                                <span>
                                    {row.cell.value ? moment(row.cell.value).format('DD/MM/YY') : '-'}
                                </span>
                            );
                        }
                    },
                    {
                        Header: 'Expiry Date',
                        accessor: 'expiryDate',
                        Cell: (row: any) => {
                            return (
                                <span>
                                    {row.cell.value ? moment(row.cell.value).format('DD/MM/YY') : '-'}
                                </span>
                            );
                        }
                    },
                    {
                        Header: 'Insurer',
                        accessor: 'insurerName',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Broker',
                        accessor: 'brokerName',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Staff Name',
                        accessor: 'staffName',
                        Cell: (row: any) => {
                            return (
                                <span>
                                    {row.cell.value ? row.cell.value : '-'}
                                </span>
                            );
                        }
                    },
                    {
                        Header: 'Created On',
                        accessor: 'createdOn',
                        Cell: (row: any) => {
                            return (
                                <span>
                                    {row.cell.value ? moment(row.cell.value).format('DD/MM/YY h:mm a') : '-'}
                                </span>
                            );
                        }
                    },
                    // {
                    //     Header: 'Status',
                    //     accessor: 'active',
                    //     Cell: (row: any) => {
                    //         return (
                    //             <div className="w-full flex text-white">
                    //                 <span
                    //                     className={clsx(
                    //                         'w-[30px] h-[30px] rounded-full flex items-center justify-center text-[0px] ',
                    //                         {
                    //                             ['bg-status-active']:
                    //                                 new Policy(row.row.original).getStatus()?.toLowerCase() ===
                    //                                 POLICY_STATUS_OPTIONS.ACTIVE
                    //                         },
                    //                         {
                    //                             ['bg-status-ended']:
                    //                                 new Policy(row.row.original).getStatus()?.toLowerCase() ===
                    //                                 POLICY_STATUS_OPTIONS.INACTIVE
                    //                         }
                    //                     )}>
                    //                     {new Policy(row.row.original).getStatus()}
                    //                 </span>
                    //             </div>
                    //         );
                    //     }
                    // },
                    {
                        Header: 'Action',
                        id: 'Action',
                        Cell: (row: any) => {
                            return (
                                <GTableActionColumn actions={getTableRowActions(row.row.original)} />
                            );
                        }
                    }
                ]
            }
        ],
        [countryId]
    );

    const mobileColumns = React.useMemo(
        () => [
            {
                Header: 'M',
                columns: [
                    {
                        Header: 'Insurer Policy #',
                        accessor: 'insurerPolicyNumber',
                        Cell: (row: any) => {
                            return (
                                <RouteLink to={`${getDashboardBase()}/policies/${row?.row?.original.policyNumber}?type=non-motor`}>
                                    {row.cell.value ?? '-'}
                                </RouteLink>
                            );
                        }
                    },
                    {
                        Header: 'Customer',
                        accessor: 'customerFullName',
                        Cell: (row: any) => {
                            return (
                                <Link
                                    to={`${getDashboardBase()}/customers/${row?.row?.original?.customerId
                                        }`}
                                    className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink" target="_blank">
                                    {row?.cell.value ? row?.cell.value : '-'}
                                </Link>
                            );
                        }
                    },
                    {
                        Header: 'Phone #',
                        accessor: 'customerPhone',
                        Cell: (row: any) => {
                            return <span>{getPhoneNumberFromShortCodeFormat(row.cell.value) ?? '-'}</span>;
                        }
                    },
                    {
                        Header: 'Policy Number',
                        accessor: 'policyNumber',
                        Cell: (row: any) => {
                            const isRenewed = row?.row?.original?.renewed;

                            return (
                                <div className="flex items-center gap-1">
                                    <Link to={`${getDashboardBase()}/policies/${row?.row?.original.ref}?filter=${filter}&page=${currentPage}`}>
                                        <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                                    </Link>

                                    {isRenewed && (
                                        <Tooltip message="Renewed policy" placement='bottom'>
                                            <span className="inline-flex items-center justify-center w-4 h-4 ml-1 text-xs font-bold text-green-600 bg-green-50 rounded-sm border border-green-600 dark:text-green-400 dark:bg-green-900/20 dark:border-green-400 transition-colors duration-200">
                                                r
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                            );
                        }
                    },
                    {
                        Header: 'Insurer',
                        accessor: 'insurerName',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Broker',
                        accessor: 'brokerName',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Policy Type',
                        accessor: 'policyType',
                        Cell: (row: any) => {
                            return (
                                <PolicyTypesBadge policyType={row.cell.value} />
                            );
                        }
                    },
                    {
                        Header: 'Staff Name',
                        accessor: 'staffName',
                        Cell: (row: any) => {
                            return (
                                <span>
                                    {row.cell.value ? row.cell.value : '-'}
                                </span>
                            );
                        }
                    },
                    {
                        Header: 'Status',
                        accessor: 'reviewStage',
                        Cell: (row: any) => {
                            return (
                                <PolicyStatus
                                    status={getPolicyReviewStatus(row.cell.value)}
                                    firstSuccessfulPayment={new Policy(
                                        row?.row?.original
                                    )?.isInitialPaymentReceived()}
                                    classes="px-2 "
                                />
                            );
                        }
                    },
                    {
                        Header: "Action",
                        id: 'Action',
                        Cell: (row: any) => {
                            return (
                                <GTableActionColumn actions={getTableRowActions(row.row.original)} />
                            );
                        }
                    }
                ]
            }
        ],
        []
    );


    const handlePageChange = (page: number) => {

        getOrUpdateQueriesFromUrl(page, searchParams, tableFilters, queriesList, queriesName)
        const updatedParams = new URLSearchParams(searchParams);
        updatedParams.set("type", "non-motor");
        updatedParams.set("page", page.toString());
        setSearchParams(updatedParams);
    };

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['getNonAutoPolicies', active, brokerId, searchParam, policyType, insurerId, reviewStage, currentPage, showPerPage, managerId, startsOn, endsOn, expiredTo, expiredFrom],
        queryFn: getNonAutoPolicies,
        staleTime: 1000 * 10,
        placeholderData: (prev) => prev,
        gcTime: 1000 * 60 * 30 * 5,
        select: (response) => {
            return {
                policies: response.content ?? [],
                totalRecords: response.totalRecords,
                pagination: {
                per_page: response.size,
                total: response.totalElements,
                totalPages: response.totalPages,
                from: response.pageable?.offset + 1,
                to: response?.totalElements > response?.pageable?.offset + showPerPage
                    ? response?.pageable?.offset + showPerPage : response?.totalElements,
                },
            };
        },

    });

    const pagination = data?.pagination;
    const policies = React.useMemo(() => {
        return data?.policies?.map((p: any) => new NonAutoPolicySummary(p));
    }, [data?.policies]);

    const getPolicyTable = () => {
        return (
            <>

                <div>
                    <GTable
                        selectionChanged={(selectedObjects: Policy[], selectedIndexes: number[]) => {
                            const policyIdList = selectedObjects.map((policy) => policy.ref);
                        }}
                        columns={screenWidth <= 550 ? mobileColumns : columns}
                        data={policies ?? []}
                        isRowLinked={false}
                        tableType="policies"
                        onSearch={(searchString: string) => setSearchString(searchString)}
                        searchPlaceholder={'Search by name, policy number or registration number...'}
                        paginationData={pagination}
                        onPageChange={handlePageChange}
                        showPerPage={showPerPage}
                        onShowPerPageChange={(page: number) => setShowPerPage(page)}
                        networkError={networkError}
                        loading={isLoading}
                        tableContainerId="tableId"
                        selectedFilters={tableFilters}
                        onRemoveFilter={(filter: ITableFilterItem, index: number) => {
                            onRemoveFilter(filter, index, tableFilters, setFilters, setSearchParams, queriesList)
                        }}
                        showFilter={
                          <>
                              <TableFilter
                                openFilter={openFilter}
                                onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
                                content={
                                    <TableFilterForm
                                        filters={tableFilters}
                                        getValues={(filters: ITableFilterItem[]) => {
                                            setFilters(filters);
                                            updateQueriesList(filters, queriesList)
                                            setSearchParams(queriesList)
                                            setOpenFilter(false);
                                        }}
                                        onResetFilters={() => setFilters(getDefaultFilters())}
                                    />
                                }
                            />
                          </>
                        }
                    />
                </div>

            </>
        );
    };

    useQuery({ queryKey: ['getAllBrollyInsurers', countryId], queryFn: getAllBrollyInsurers })

    useQuery({ queryKey: ['getAllBrollyBrokers', countryId], queryFn: getAllBrollyBrokers })


    return (
        <>
            {policiesCount?.nonMotorPolicies === 0 && !isFetching && user.isBroker() && <div className='py-4'>
                <Banner
                // key={index}
                variant={'info'}
                message={"It looks like you haven't added any policy yet. To begin managing your portfolio, you can:"}
                position="top"
                isSticky={true}
                dismissible={true}
                onDismiss={() => {}}
                action={{
                    label: 'Add an existing policy',
                    onClick: () => {
                        navigate(`${getDashboardBase()}/policies/dynamic-create-policy?type=non-motor`);
                    }
                }}
                />
            </div>}
       
            <Page title="Policies">
                <div ref={pageRef} className="bg-white dark:bg-darkBg w-full p-5 min-h-full rounded-lg shadow-md dark:shadow-darkBg-shadow">
                    {/* <div className="flex flex-col md:flex-row justify-between md:items-center"> */}
                    <div>
                        <div className="inline-flex items-center gap-2 mb-2 dark:text-darkBg-mainText">
                            <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                            <h2 className="font-semibold text-3xl">
                                Non-Motor Insurance Policies
                            </h2>
                        </div>


                        <p className="text-sm text-slate-400">
                            View a list of all non-motor policies and associated details.
                        </p>
                    </div>

                    {/* Create new policy */}
                    {/* {((user.isAdministrator() || user.isInsurer() || user.isAgency() || user.isBroker()) && !user.isInternalAdmin()) &&
                            <div className="flex gap-3 mt-5 md:mt-0">
                                <Button
                                    bgColor="primary"
                                    label={'Add Policy'}
                                    icon={<Plus className='h-5 w-5' />}
                                    onClick={() => navigate(`${getDashboardBase()}/policies/dynamic-create-policy?type=non-motor`)}
                                />
                            </div>
                        } */}
                    {/* </div> */}

                    <div className="mt-5">{getPolicyTable()}</div>
                </div>
                <DownloadHookModal />
                <PolicyModalComponent />
                <ModalComponent />
                <ClaimModal />
            </Page>
        </>
    );
}

export default Policies;
